import { AutoOpenConfig } from './types';
import { dlog, matchesAnyUrlPattern, pageUrl, session } from './util';

// Merchant-configured auto-open (docs/widget.md). When enabled the panel pops
// open on its own after `delaySeconds` on pages whose URL matches `pages`
// (empty = every page). It fires at most once per browser session and never
// fights the visitor: once they've opened the panel themselves — or it has
// auto-opened once — it stays quiet for the rest of the session. Re-checked on
// SPA route changes so a match on a later page still triggers.

const SESSION_KEY_PREFIX = 'cb_autoopen_';

export class AutoOpen {
  private timer: ReturnType<typeof setTimeout> | undefined;
  private done: boolean;
  private readonly key: string;

  constructor(
    private readonly config: AutoOpenConfig,
    private readonly isOpen: () => boolean,
    private readonly open: () => void,
    siteKey: string,
  ) {
    this.key = SESSION_KEY_PREFIX + siteKey;
    // already fired in this browser session (survives same-tab navigations)
    this.done = session.get(this.key) === '1';
  }

  /** Call on boot and on every SPA route change. Schedules the open when the
   *  current page matches; cancels a pending open if we navigate away first. */
  evaluate(): void {
    if (this.done || !this.config.enabled) return;

    if (!matchesAnyUrlPattern(this.config.pages, pageUrl())) {
      this.cancel(); // left a matching page before the timer fired
      return;
    }
    if (this.timer !== undefined) return; // already scheduled for the current match

    const delay = Math.max(0, this.config.delaySeconds) * 1000;
    this.timer = setTimeout(() => this.fire(), delay);
    dlog('auto-open scheduled', { delayMs: delay });
  }

  /** The visitor interacted with the widget themselves → stand down for the session. */
  suppress(): void {
    this.done = true;
    session.set(this.key, '1');
    this.cancel();
  }

  private fire(): void {
    this.timer = undefined;
    if (this.done) return;
    this.done = true;
    session.set(this.key, '1'); // once per session, even across navigations
    if (!this.isOpen()) {
      dlog('auto-open firing');
      this.open();
    }
  }

  private cancel(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }
}
